/** @jsxImportSource @emotion/react */
import { css } from '@emotion/react'

import React from 'react'
import { View, Text, Divider, Image } from '@components/atoms'
import { ShopOutline } from '@components/molecules'
import { Footer, Seo } from '@components/systems'
import { buildProductJsonLd, buildBreadcrumbJsonLd } from '@utils'
import { SEO_SITE, getSeoEntry } from '@src/configs/seo.config'

import gal1 from '@public/images/pages/shop/dualSwitch/gallery/ds-gallery1.jpg'
import gal2 from '@public/images/pages/shop/dualSwitch/gallery/ds-gallery2.jpg'
import gal3 from '@public/images/pages/shop/dualSwitch/gallery/ds-gallery3.jpg'

import thumb1 from '@public/images/pages/shop/dualSwitch/thumbnail/ds-thumb1.jpg'
import thumb2 from '@public/images/pages/shop/dualSwitch/thumbnail/ds-thumb2.jpg'
import thumb3 from '@public/images/pages/shop/dualSwitch/thumbnail/ds-thumb3.jpg'

import chaneelImg from '@public/images/pages/shop/amp2/contents/channel.png'

import { useAppSelector } from '@redux/index'
import { SeasonProduct, SeasonProductType } from '@schemas'
import { grey } from '@mui/material/colors'

const ShopDualSwitchPage = () => {
	const now = new Date()

	const seasonProductList: SeasonProductType[] = useAppSelector(
		state => state.shop.productList,
	)
	const thisProduct: SeasonProductType = React.useMemo(() => {
		const filterRes: SeasonProductType[] = seasonProductList.filter(
			aProduct => aProduct.productCode === 'DS',
		)

		if (filterRes.length === 0) {
			return new SeasonProduct()
		}

		return filterRes[0]
	}, [seasonProductList])

	const images = [
		{
			original: gal1,
			thumbnail: thumb1,
		},
		{
			original: gal2,
			thumbnail: thumb2,
		},
		{
			original: gal3,
			thumbnail: thumb3,
		},
	]

	const [qty, setQty] = React.useState<number>(1)

	const jsonLd = React.useMemo(
		() => [
			buildProductJsonLd({
				name: 'Judahs Dual Switch',
				description: getSeoEntry('/shop/ds').description,
				path: '/shop/ds',
				image: `${SEO_SITE.origin}${gal1}`,
				product: thisProduct,
				sku: 'DS',
			}),
			buildBreadcrumbJsonLd([
				{ name: 'Home', path: '/' },
				{ name: 'Shop', path: '/shop' },
				{ name: 'Judahs Dual Switch', path: '/shop/ds' },
			]),
		],
		[thisProduct],
	)

	return (
		<View css={style}>
			<Seo jsonLd={jsonLd} />

			<View className="shopDetailOuterView">
				<View className="shopDetailInnerView">
					<ShopOutline
						title="Judahs Dual Switch"
						subtitle="TRS Momentary Footswitch Expansion"
						images={images}
						qty={qty}
						onQtyChange={newQty => setQty(newQty)}
						product={thisProduct}
					/>

					<View className="shopDetailContentViwe">
						<View className="articleView">
							<Text category="h1">Product Description</Text>
							<p className="contentText">
								The <b>Judahs Dual Switch</b> is a{' '}
								<b>handcrafted by Judahs’ professional developers.</b>
								This unit features <b>two momentary footswitches</b> and can be used by
								connecting a <b>TRS cable</b>. Simply connect the Dual Switch to any
								compatible Judahs product to instantly increase the number of available
								switches—perfect for players who want more control, more flexibility, and
								a streamlined performance setup.
							</p>
							<p className="contentText">
								<b>Key Features:</b>
							</p>

							<ul>
								<li>Two smooth and durable momentary footswitches</li>
								<li>1/4-inch TRS input jack for easy integration</li>
								<li>Professionally handcrafted by skilled makers</li>
								<li>Designed specifically for Judahs products</li>
							</ul>
						</View>
					</View>
				</View>
			</View>

			<Footer />
		</View>
	)
}

export default ShopDualSwitchPage

const style = css({
	width: '100%',

	'.shopDetailOuterView': {
		borderBottom: '1px solid',
		borderColor: grey[900],

		'.shopDetailInnerView': {
			width: '80rem',
			margin: 'auto',

			'.shopDetailContentViwe': {
				width: 'calc(100% - 30rem)',
				textAlign: 'left',

				'.userManualView': {
					'.userManualOpenBtn': {
						marginRight: '0.5rem',
					},
				},

				'.articleView': {
					marginTop: '6rem',
					marginBottom: '6rem',

					'.contentTitle': {
						fontWeight: '900',
						fontSize: '22px',
						lineHeight: 1.5,
					},

					'.contentText': {
						marginBottom: '0',
						fontSize: '22px',
						lineHeight: 1.5,
					},

					'.subarticleView': {
						marginBottom: '4rem',

						li: {
							fontSize: '22px',
							lineHeight: 1.5,
						},
					},
					'.subarticleContentView': {
						width: '100%',
						marginTop: '1rem',
						textAlign: 'center',
						'.contentImage': {
							width: '50%',
						},

						'.contentChannelImage': {
							width: '100%',
							border: '1px solid',
							borderColor: grey['500'],
						},
					},
				},

				'.contentView': {
					width: '100%',
					marginTop: '2rem',
					textAlign: 'center',

					'.contentImage1': {
						width: '60%',
					},
					'.contentVideo': {
						width: '100%',
					},
				},
			},
		},
	},

	'@media (max-width: 1350px)': {
		'.shopDetailOuterView': {
			padding: '0 2rem',
			'.shopDetailInnerView': {
				width: '100%',
			},
		},
	},

	'@media (max-width: 900px)': {
		'.shopDetailOuterView': {
			'.shopDetailInnerView': {
				'.shopDetailContentViwe': {
					width: '100%',

					'.articleView': {
						'.subarticleContentView': {
							'.contentImage': {
								width: '80%',
							},
						},
					},

					'.contentView': {
						width: '100%',
						textAlign: 'center',

						'.contentImage1': {
							width: '80%',
						},
					},
				},
			},
		},
	},
})
